import { useState, useEffect } from "react";
import { AppHeader } from "@/components/AppHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { User, Mail, Phone, Shield } from "lucide-react";

const Profile = () => {
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    phone: "",
    specialty: ""
  });
  
  useEffect(() => {
    // Load user data from localStorage
    const userData = localStorage.getItem('fruitycall_user');
    if (userData) {
      const parsed = JSON.parse(userData);
      setProfile({
        name: parsed.name || "Dr. Jean Dupont",
        email: parsed.email || "docteur@exemple.fr",
        phone: "01 23 45 67 89",
        specialty: "Médecine générale"
      });
    }
  }, []);
  
  const handleChange = (e) => {
    setProfile({
      ...profile,
      [e.target.name]: e.target.value
    });
  };
  
  const handleSaveProfile = (e) => {
    e.preventDefault();
    // Update localStorage
    localStorage.setItem('fruitycall_user', JSON.stringify({
      name: profile.name,
      email: profile.email
    }));
    toast.success("Profil mis à jour avec succès");
  };
  
  return (
    <div className="flex min-h-screen flex-col bg-muted/20">
      <AppHeader />
      
      <main className="flex-1">
        <div className="container-custom py-8">
          <div className="mb-8">
            <h1 className="mb-2 text-3xl font-bold">Mon profil</h1>
            <p className="text-muted-foreground">
              Gérez vos informations personnelles et professionnelles
            </p>
          </div>
          
          <div className="mx-auto max-w-2xl space-y-6">
            {/* Profile Information */}
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  <CardTitle>Informations du compte</CardTitle>
                </div>
                <CardDescription>
                  Vos informations professionnelles visibles sur votre compte
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveProfile} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nom complet</Label>
                    <Input
                      id="name"
                      name="name"
                      value={profile.name}
                      onChange={handleChange}
                      placeholder="Dr. Jean Dupont"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="flex gap-2">
                      <Mail className="mt-2.5 h-5 w-5 text-muted-foreground" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={profile.email}
                        onChange={handleChange}
                        placeholder="votre@email.fr"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone</Label>
                    <div className="flex gap-2">
                      <Phone className="mt-2.5 h-5 w-5 text-muted-foreground" />
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={profile.phone}
                        onChange={handleChange}
                        placeholder="01 23 45 67 89"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="specialty">Spécialité</Label>
                    <Input
                      id="specialty"
                      name="specialty"
                      value={profile.specialty}
                      onChange={handleChange}
                      placeholder="Médecine générale"
                    />
                  </div>
                  
                  <Button type="submit" size="lg" className="w-full">
                    Enregistrer les modifications
                  </Button>
                </form>
              </CardContent>
            </Card>
            
            {/* Account Summary */}
            <Card className="border-2 bg-gradient-to-br from-primary/5 to-secondary/10">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  <CardTitle>Résumé du compte</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-lg border bg-background p-4">
                    <div className="mb-1 text-2xl font-bold text-primary">Actif</div>
                    <div className="text-sm text-muted-foreground">Statut du compte</div>
                  </div>
                  <div className="rounded-lg border bg-background p-4">
                    <div className="mb-1 text-2xl font-bold text-primary">
                      {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                    </div>
                    <div className="text-sm text-muted-foreground">Membre depuis</div>
                  </div>
                </div>
                
                <div className="mt-4 rounded-lg border bg-background p-4">
                  <h3 className="mb-2 font-semibold text-foreground">Sécurité et confidentialité</h3>
                  <p className="text-sm text-muted-foreground">
                    Votre compte est protégé par un chiffrement de bout en bout. Toutes vos données
                    sont stockées de manière sécurisée conformément au RGPD et aux normes HDS.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {/* Quick Actions */}
            <Card className="border-2">
              <CardHeader>
                <CardTitle>Actions rapides</CardTitle>
                <CardDescription>
                  Accès rapide aux fonctionnalités principales
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-2">
                <Button
                  variant="outline"
                  className="h-auto flex-col items-start gap-1 p-4"
                  onClick={() => window.location.href = '/app/settings'}
                >
                  <span className="font-semibold">Paramètres</span>
                  <span className="text-xs text-muted-foreground">Notifications et préférences</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto flex-col items-start gap-1 p-4"
                  onClick={() => toast.info("Fonctionnalité en cours de développement")}
                >
                  <span className="font-semibold">Historique</span>
                  <span className="text-xs text-muted-foreground">Consulter l'historique complet</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto flex-col items-start gap-1 p-4"
                  onClick={() => window.location.href = '/security'}
                >
                  <span className="font-semibold">Sécurité</span>
                  <span className="text-xs text-muted-foreground">En savoir plus sur la sécurité</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto flex-col items-start gap-1 p-4"
                  onClick={() => window.location.href = '/contact'}
                >
                  <span className="font-semibold">Support</span>
                  <span className="text-xs text-muted-foreground">Contacter l'équipe</span>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;